import axios from 'axios';

// Base URL API
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Axios instance configurata
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor per gestire errori
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth Service
export const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  logout: async () => {
    await api.post('/auth/logout');
  },
};

// Users Service (Admin only)
export const usersService = {
  getAll: async (includeInactive = false) => {
    const response = await api.get('/auth/users', {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  getById: async (userId) => {
    const response = await api.get(`/auth/users/${userId}`);
    return response.data;
  },

  create: async (userData) => {
    const response = await api.post('/auth/users', userData);
    return response.data;
  },

  update: async (userId, userData) => {
    const response = await api.put(`/auth/users/${userId}`, userData);
    return response.data;
  },

  delete: async (userId) => {
    const response = await api.delete(`/auth/users/${userId}`);
    return response.data;
  },

  resetPassword: async (userId, newPassword) => {
    const response = await api.put(`/auth/users/${userId}/password`, {
      new_password: newPassword,
    });
    return response.data;
  },
};

// Sensors Service
export const sensorsService = {
  getAll: async (includeOffline = true) => {
    const response = await api.get('/sensors/', {
      params: { include_offline: includeOffline },
    });
    return response.data;
  },

  getSummary: async () => {
    const response = await api.get('/sensors/summary');
    return response.data;
  },

  getById: async (sensorId) => {
    const response = await api.get(`/sensors/${sensorId}`);
    return response.data;
  },

  create: async (sensorData) => {
    const response = await api.post('/sensors/', sensorData);
    return response.data;
  },

  update: async (sensorId, sensorData) => {
    const response = await api.put(`/sensors/${sensorId}`, sensorData);
    return response.data;
  },

  delete: async (sensorId) => {
    const response = await api.delete(`/sensors/${sensorId}`);
    return response.data;
  },

  setReference: async (sensorId, data) => {
    const response = await api.put(`/sensors/${sensorId}`, { initial_latitude: data.latitude, initial_longitude: data.longitude, initial_altitude: data.altitude });
    return response.data;
  },

  resetAllData: async () => {
    const response = await api.post("/sensors/reset-all-data");
    return response.data;
  },

  updateAlarmConfig: async (sensorId, alarmType, configData) => {
    const response = await api.put(
      `/sensors/${sensorId}/alarm-config/${alarmType}`,
      configData
    );
    return response.data;
  },

  getConnectionStatus: async (sensorId) => {
    const response = await api.get(`/sensors/${sensorId}/connection-status`);
    return response.data;
  },
};

// Measurements Service
export const measurementsService = {
  getLatest: async (sensorId) => {
    const response = await api.get(`/measurements/${sensorId}/latest`);
    return response.data;
  },

  getHistory: async (sensorId, params = {}) => {
    const response = await api.get(`/measurements/${sensorId}`, { params });
    return response.data;
  },

  getDailyStats: async (sensorId, params = {}) => {
    const response = await api.get(`/measurements/${sensorId}/daily`, { params });
    return response.data;
  },

  getHourlyStats: async (sensorId, params = {}) => {
    const response = await api.get(`/measurements/${sensorId}/hourly`, { params });
    return response.data;
  },

  getTimeSeries: async (sensorId, params = {}) => {
    const response = await api.get(`/measurements/${sensorId}/time-series`, { params });
    return response.data;
  },
};

// Displacements Service
export const displacementsService = {
  getAllLatest: async () => {
    const response = await api.get('/displacements/latest');
    return response.data;
  },

  getHistory: async (sensorId, params = {}) => {
    const response = await api.get(`/displacements/${sensorId}`, { params });
    return response.data;
  },

  getLatest: async (sensorId) => {
    const response = await api.get(`/displacements/${sensorId}/latest`);
    return response.data;
  },

  getTimeSeries: async (sensorId, params = {}) => {
    const response = await api.get(`/displacements/${sensorId}/time-series`, { params });
    return response.data;
  },

  getTrend: async (sensorId, days = 30) => {
    const response = await api.get(`/displacements/${sensorId}/trend`, {
      params: { days },
    });
    return response.data;
  },

  getArrow: async (sensorId, scale = 1000) => {
    const response = await api.get(`/displacements/${sensorId}/arrow`, {
      params: { scale },
    });
    return response.data;
  },

  getAllArrows: async (scale = 1000) => {
    const response = await api.get('/displacements/map/arrows', {
      params: { scale },
    });
    return response.data;
  },
};

// Alarms Service
export const alarmsService = {
  getActive: async (params = {}) => {
    const response = await api.get('/alarms/', { params });
    return response.data;
  },

  getSummary: async () => {
    const response = await api.get('/alarms/summary');
    return response.data;
  },

  getHistory: async (params = {}) => {
    const response = await api.get('/alarms/history', { params });
    return response.data;
  },

  acknowledge: async (alarmId, user) => {
    const response = await api.post(`/alarms/${alarmId}/acknowledge`, { user });
    return response.data;
  },

  resolve: async (alarmId) => {
    const response = await api.post(`/alarms/${alarmId}/resolve`);
    return response.data;
  },
};

// WebSocket connection
export const createWebSocket = (onMessage, onConnect, onDisconnect) => {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/live`;

  let ws = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;

  const connect = () => {
    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
      reconnectAttempts = 0;
      if (onConnect) onConnect();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type !== 'ping' && data.type !== 'pong') {
          if (onMessage) onMessage(data);
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (onDisconnect) onDisconnect();

      // Auto reconnect
      if (reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++;
        setTimeout(connect, 2000 * reconnectAttempts);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  };

  connect();

  return {
    send: (data) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(data));
      }
    },
    close: () => {
      if (ws) {
        ws.close();
      }
    },
    subscribe: (sensorId) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'subscribe', sensor_id: sensorId }));
      }
    },
  };
};

export default api;
